import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ETypographySize, TypographySize } from '../../index';

enum EAccordionColor {
	white = 'bg-white',
	'gray-600' = 'bg-gray-600',
	'gray-700' = 'bg-gray-700',
}

type AccordionColor = keyof typeof EAccordionColor;

interface IAccordionProps {
	chevronColor?: string;
	children: React.ReactNode;
	className?: string;
	classNameOpen?: string;
	color?: AccordionColor;
	title?: React.ReactNode;
	titleSize?: TypographySize;
	borderBottom?: boolean;
	defaultOpen?: boolean;
	preventAnimation?: boolean;
}

export const Accordion = ({
	chevronColor = '#121212',
	children,
	className,
	classNameOpen,
	color = 'white',
	title,
	titleSize = 'lg',
	borderBottom = false,
	defaultOpen = false,
	preventAnimation = false,
}: IAccordionProps) => {
	const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);
	const [animate, setAnimate] = useState<boolean>(false);
	const accordionRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (isOpen) {
			setAnimate(true);

			if (preventAnimation) return;

			if (accordionRef.current) {
				const bottomPos = accordionRef.current.getBoundingClientRect().bottom;
				if (window.innerHeight < bottomPos) {
					accordionRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
				}
			}
		}
	}, [isOpen]);

	return (
		<div
			ref={accordionRef}
			className={`${EAccordionColor[color]} rounded  ${
				borderBottom ? 'border-b border-t-0 border-l-0 border-r-0 border-gray-500' : 'border border-gray-600'
			} border-solid transition-colors duration-150 focus-visible:outline-primary ${className}`}>
			<div
				className={`cursor-pointer inline-flex items-center justify-between ${
					borderBottom ? '' : ' px-3'
				} rounded w-full ${EAccordionColor[color]} font-bold ${ETypographySize[titleSize]} text-gray-100 ${
					isOpen ? 'border-b-0 rounded-b-none' : ''
				}`}
				role="button"
				aria-expanded={isOpen}
				aria-controls={`section-${title}`}
				tabIndex={0}
				onClick={() => {
					if (isOpen) {
						setAnimate(false);
					} else {
						setIsOpen(true);
					}
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						if (isOpen) {
							setAnimate(false);
						} else {
							setIsOpen(true);
						}
					}
				}}>
				<span className="first-letter:uppercase">{title}</span>

				<ChevronDown large fill={chevronColor} rotate={animate} />
			</div>
			{isOpen && (
				<div
					id={`accordion-${title}`}
					role="region"
					aria-labelledby={`acc-${title}`}
					className={`${
						animate ? 'animate-fadeSlideInFast' : 'animate-fadeSlideOutFast'
					} h-auto overflow-hidden ${classNameOpen}`}
					onAnimationEnd={() => {
						if (!animate) {
							setIsOpen(false);
						}
					}}>
					{children}
				</div>
			)}
		</div>
	);
};
